/* eslint-disable @typescript-eslint/no-shadow */
import {Injectable} from '@angular/core';
import {RIGHTS_INVENTORY_ON_PAGE_ITEMS} from '@entities/rights/constants';
import {RightsInventoryRequest} from '@entities/rights/rights-api-interfaces';
import {
	Inventory,
	InventoryFilters,
	RightsInventoryBuffer,
	RightsSourcesOption,
	RightsView,
	WorkflowStatus,
} from '@entities/rights/rights.interfaces';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {Action, Store} from '@ngrx/store';
import {RightsApiService} from '@pages/rights/api/rights.api.service';
import {DEFAULT_ACTIONS_DEBOUNCE_MS, DEFAULT_API_CHANGED_MS} from '@shared/constants/debounce.constants';
import {RightsHelper} from '@shared/helpers/rights.helper';
import {ExportExcelService} from '@shared/services/export-to-excel.service';
import {ReportsApiService} from '@shared/services/reports.api.service';
import {AppState} from '@store';
import {PinnedDataHelper} from '@store/rights/helpers/pinned-data.helper';
import {routerSelectors} from '@store/route/selectors';
import {saveAs} from 'file-saver-es';
import {EMPTY, ObservableInput, ObservedValueOf, OperatorFunction, of} from 'rxjs';
import {
	catchError,
	concatMap,
	concatMapTo,
	debounceTime,
	distinctUntilChanged,
	filter,
	map,
	mapTo,
	switchMap,
	withLatestFrom,
} from 'rxjs/operators';
import {rightsActions} from './actions';
import {waitFiltersReady} from './helpers/rights-store.helper';
import {rightsSelectors} from './selectors';

// withLatestFrom declaration for 7 arguments
declare module 'rxjs/internal/operators/withLatestFrom' {
	/**
	 * withLatestFrom Type
	 * @param {O2} v2
	 * @param {O3} v3
	 * @param {O4} v4
	 * @param {O5} v5
	 * @param {O6} v6
	 * @param {O7} v7
	 * @returns {OperatorFunction<
			T,
			[
				T,
				ObservedValueOf<O2>,
				ObservedValueOf<O3>,
				ObservedValueOf<O4>,
				ObservedValueOf<O5>,
				ObservedValueOf<O6>,
				ObservedValueOf<O7>
			]
		>}
	 */
	export function withLatestFrom<
		T,
		O2 extends ObservableInput<any>,
		O3 extends ObservableInput<any>,
		O4 extends ObservableInput<any>,
		O5 extends ObservableInput<any>,
		O6 extends ObservableInput<any>,
		O7 extends ObservableInput<any>
	>(
		v2: O2,
		v3: O3,
		v4: O4,
		v5: O5,
		v6: O6,
		v7: O7
	): OperatorFunction<
		T,
		[
			T,
			ObservedValueOf<O2>,
			ObservedValueOf<O3>,
			ObservedValueOf<O4>,
			ObservedValueOf<O5>,
			ObservedValueOf<O6>,
			ObservedValueOf<O7>
		]
	>;
}

/**
 * This class is an injectable Angular service that provides a way to fetch data for Rights Page.
 */
@Injectable()
export class RightsEffects {
	/**
	 * This function creates an Angular effect that get rights sources.
	 * @returns an Observable that emits data when the effect completes
	 */
	getRightsSources$ = createEffect(() =>
		this.actions$.pipe(
			ofType(rightsActions.rightsSources.requested),
			switchMap(() =>
				this.rightsApiService.getRightsSources().pipe(
					concatMap((sources) => [
						rightsActions.rightsSources.succeeded({sources}),
						rightsActions.source({source: sources[0]}),
					]),
					catchError((error) => of(rightsActions.rightsSources.failed({error})))
				)
			)
		)
	);

	/**
	 * This function creates an Angular effect that change rights source.
	 * @returns an Observable that emits data when the effect completes
	 */
	changeRightsSource$ = createEffect(() =>
		this.actions$.pipe(
			ofType(rightsActions.source),
			concatMap(() => [
				rightsActions.setInventoryFilters({filters: null}),
				rightsActions.getLanguages.requested(),
				rightsActions.getChannels.requested(),
				rightsActions.getGenres.requested(),
				rightsActions.getLanguagesMetadata.requested(),
				rightsActions.getTerritories.requested(),
				rightsActions.getMediaTypes.requested(),
				rightsActions.getBrands.requested(),
				rightsActions.getProjectTypes.requested(),
				rightsActions.getProductOwners.requested(),
				rightsActions.getWorkflowStatuses.requested(),
			])
		)
	);

	/**
	 * This function creates an Angular effect that change rights view.
	 * @returns an Observable that emits data when the effect completes
	 */
	changeRightsView$ = createEffect(() =>
		this.actions$.pipe(
			ofType(rightsActions.setRightsView),
			concatMapTo([
				rightsActions.getLanguages.requested(),
				rightsActions.getChannels.requested(),
				rightsActions.getGenres.requested(),
				rightsActions.getLanguagesMetadata.requested(),
				rightsActions.getTerritories.requested(),
				rightsActions.getMediaTypes.requested(),
			])
		)
	);

	/**
	 * This function creates an Angular effect that get media types.
	 * @returns an Observable that emits data when the effect completes
	 */
	getMediaTypes$ = createEffect(() =>
		this.actions$.pipe(
			ofType(rightsActions.getMediaTypes.requested),
			withLatestFrom(
				this.store.select(rightsSelectors.rightsSources.selected),
				this.store.select(rightsSelectors.rightsSources.view)
			),
			filter((data) => !!data[1]),
			switchMap(([, franchise, view]: [Action, RightsSourcesOption, RightsView]) =>
				this.rightsApiService.getMediaTypes({FRANCHISE_GROUP_CODE: franchise.id, MODE: view}).pipe(
					map((mediaTypes) => rightsActions.getMediaTypes.succeeded({mediaTypes})),
					catchError((error) => of(rightsActions.getMediaTypes.failed({error})))
				)
			)
		)
	);

	/**
	 * This function creates an Angular effect that get territories.
	 * @returns an Observable that emits data when the effect completes
	 */
	getTerritories$ = createEffect(() =>
		this.actions$.pipe(
			ofType(rightsActions.getTerritories.requested),
			withLatestFrom(
				this.store.select(rightsSelectors.rightsSources.selected),
				this.store.select(rightsSelectors.rightsSources.view)
			),
			filter((data) => !!data[1]),
			switchMap(([, franchise, view]: [Action, RightsSourcesOption, RightsView]) =>
				this.rightsApiService.getTerritories({FRANCHISE_GROUP_CODE: franchise.id, MODE: view}).pipe(
					map((territories) => rightsActions.getTerritories.succeeded({territories})),
					catchError((error) => of(rightsActions.getTerritories.failed({error})))
				)
			)
		)
	);

	/**
	 * This function creates an Angular effect that get languages.
	 * @returns an Observable that emits data when the effect completes
	 */
	getLanguages$ = createEffect(() =>
		this.actions$.pipe(
			ofType(rightsActions.getLanguages.requested),
			withLatestFrom(
				this.store.select(rightsSelectors.rightsSources.selected),
				this.store.select(rightsSelectors.rightsSources.view)
			),
			filter((data) => !!data[1]),
			switchMap(([, franchise, view]: [Action, RightsSourcesOption, RightsView]) =>
				this.rightsApiService.getLanguages({FRANCHISE_GROUP_CODE: franchise.id, MODE: view}).pipe(
					map((languages) => rightsActions.getLanguages.succeeded({languages})),
					catchError((error) => of(rightsActions.getLanguages.failed({error})))
				)
			)
		)
	);

	/**
	 * This function creates an Angular effect that get channels.
	 * @returns an Observable that emits data when the effect completes
	 */
	getChannels$ = createEffect(() =>
		this.actions$.pipe(
			ofType(rightsActions.getChannels.requested),
			withLatestFrom(
				this.store.select(rightsSelectors.rightsSources.selected),
				this.store.select(rightsSelectors.rightsSources.view)
			),
			filter((data) => !!data[1]),
			switchMap(([, franchise, view]: [Action, RightsSourcesOption, RightsView]) =>
				this.rightsApiService.getChannels({FRANCHISE_GROUP_CODE: franchise.id, MODE: view}).pipe(
					map((channels) => rightsActions.getChannels.succeeded({channels})),
					catchError((error) => of(rightsActions.getChannels.failed({error})))
				)
			)
		)
	);

	/**
	 * This function creates an Angular effect that get genres.
	 * @returns an Observable that emits data when the effect completes
	 */
	getGenres$ = createEffect(() =>
		this.actions$.pipe(
			ofType(rightsActions.getGenres.requested),
			withLatestFrom(
				this.store.select(rightsSelectors.rightsSources.selected),
				this.store.select(rightsSelectors.rightsSources.view)
			),
			filter((data) => !!data[1]),
			switchMap(([, franchise, view]: [Action, RightsSourcesOption, RightsView]) =>
				this.rightsApiService.getGenres({FRANCHISE_GROUP_CODE: franchise.id, MODE: view}).pipe(
					map((genres) => rightsActions.getGenres.succeeded({genres})),
					catchError((error) => of(rightsActions.getGenres.failed({error})))
				)
			)
		)
	);

	/**
	 * This function creates an Angular effect that get Languages Metadata.
	 * @returns an Observable that emits data when the effect completes
	 */
	getLanguagesMetadata$ = createEffect(() =>
		this.actions$.pipe(
			ofType(rightsActions.getLanguagesMetadata.requested),
			withLatestFrom(
				this.store.select(rightsSelectors.rightsSources.selected),
				this.store.select(rightsSelectors.rightsSources.view)
			),
			filter((data) => !!data[1]),
			switchMap(([, franchise, view]: [Action, RightsSourcesOption, RightsView]) =>
				this.rightsApiService.getLanguagesMetadata({FRANCHISE_GROUP_CODE: franchise.id, MODE: view}).pipe(
					map((data) => rightsActions.getLanguagesMetadata.succeeded({data})),
					catchError((error) => of(rightsActions.getLanguagesMetadata.failed({error})))
				)
			)
		)
	);

	/**
	 * This function creates an Angular effect that get brands.
	 * @returns an Observable that emits data when the effect completes
	 */
	getBrands$ = createEffect(() =>
		this.actions$.pipe(
			ofType(rightsActions.getBrands.requested),
			withLatestFrom(this.store.select(rightsSelectors.rightsSources.selected)),
			filter((data) => !!data[1]),
			switchMap(([, franchise]: [Action, RightsSourcesOption]) =>
				this.rightsApiService.getBrands({FRANCHISE_GROUP_CODE: franchise.id}).pipe(
					map((brands) => rightsActions.getBrands.succeeded({brands})),
					catchError((error) => of(rightsActions.getBrands.failed({error})))
				)
			)
		)
	);

	/**
	 * This function creates an Angular effect that get project types.
	 * @returns an Observable that emits data when the effect completes
	 */
	getProjectTypes$ = createEffect(() =>
		this.actions$.pipe(
			ofType(rightsActions.getProjectTypes.requested),
			withLatestFrom(this.store.select(rightsSelectors.rightsSources.selected)),
			filter((data) => !!data[1]),
			switchMap(([, franchise]: [Action, RightsSourcesOption]) =>
				this.rightsApiService.getProjectTypes({FRANCHISE_GROUP_CODE: franchise.id}).pipe(
					map((projectTypes) => rightsActions.getProjectTypes.succeeded({projectTypes})),
					catchError((error) => of(rightsActions.getProjectTypes.failed({error})))
				)
			)
		)
	);

	/**
	 * This function creates an Angular effect that get product owners.
	 * @returns an Observable that emits data when the effect completes
	 */
	getProductOwners$ = createEffect(() =>
		this.actions$.pipe(
			ofType(rightsActions.getProductOwners.requested),
			withLatestFrom(
				this.store.select(rightsSelectors.rightsSources.selected),
				this.store.select(routerSelectors.router.queryParams),
				this.store.select(rightsSelectors.rightsSources.view)
			),
			filter((data) => !!data[1]),
			switchMap(([, franchise, queryParams, view]) =>
				this.rightsApiService.getProductOwners({FRANCHISE_GROUP_CODE: franchise.id, MODE: view}).pipe(
					map((productOwners) => {
						const newItems = queryParams.productCode
							? productOwners.filter(({id}) => id === '1' || id === '12')
							: productOwners;

						return rightsActions.getProductOwners.succeeded({
							productOwners: newItems,
						});
					}),
					catchError((error) => of(rightsActions.getProductOwners.failed({error})))
				)
			)
		)
	);

	/**
	 * This function creates an Angular effect that get workflow statuses.
	 * @returns an Observable that emits data when the effect completes
	 */
	getWorkflowStatuses$ = createEffect(() =>
		this.actions$.pipe(
			ofType(rightsActions.getWorkflowStatuses.requested),
			switchMap(() =>
				this.rightsApiService.getWorkflowStatuses().pipe(
					map((workflowStatuses) => rightsActions.getWorkflowStatuses.succeeded({workflowStatuses})),
					catchError((error) => of(rightsActions.getWorkflowStatuses.failed({error})))
				)
			)
		)
	);

	/**
	 * This function creates an Angular effect that get inventory.
	 * @returns an Observable that emits data when the effect completes
	 */
	getInventory$ = createEffect(() =>
		this.actions$.pipe(
			ofType(rightsActions.getInventory.requested),
			waitFiltersReady.call(this),
			debounceTime(DEFAULT_API_CHANGED_MS),
			withLatestFrom(
				this.store.select(rightsSelectors.inventory.data),
				this.store.select(rightsSelectors.rightsSources.selected),
				this.store.select(rightsSelectors.inventory.filters),
				this.store.select(rightsSelectors.rightsSources.view),
				this.store.select(rightsSelectors.workflowStatuses.data),
				this.store.select(rightsSelectors.pagination.currentPage)
			),
			filter(([, , franchise, filters, ,]) => !!franchise && !!filters),
			switchMap(
				([, previousValue, franchise, filters, view, workflowStatuses, currentPage]: [
					Action,
					Inventory[],
					RightsSourcesOption,
					InventoryFilters,
					RightsView,
					WorkflowStatus[],
					number
				]) => {
					if (
						!!filters.mediaType.length &&
						!!filters.territory.length &&
						!!filters.language.length &&
						!!filters.contentCode.length &&
						!!filters.projectCode.length &&
						!!filters.productCode.length &&
						!!filters.channel.length &&
						!!filters.genre.length &&
						!!filters.workflowStatus.length
					) {
						const params: RightsInventoryRequest = {
							FRANCHISE_GROUP_CODE: franchise.id,
							LEVEL_CODE: '',
							MEDIA_CODES: filters?.mediaType ?? [''],
							TERRITORY_CODES: filters?.territory ?? [''],
							LANGUAGE_CODES: filters?.language ?? [''],
							CHANNEL_CODES: filters?.channel ?? [''],
							GENRE_CODES: filters?.genre ?? [''],
							CONTENT_CODES: filters?.contentCode ?? [''],
							PROJECT_TYPE_CODES: filters?.projectCodeRequest ?? [''],
							PRODUCT_OWNER_CODES: filters?.productCodeRequest ?? [''],
							WORKFLOW_STATUSES:
								filters?.workflowStatus?.[0] === '999'
									? workflowStatuses.slice(1).map(({id}) => +id)
									: filters?.workflowStatus?.map(Number) ?? [],
							SEARCH_STRING: filters?.text ?? '',
							EXCLUSIVITY: filters?.exclusivity.toUpperCase(),
							EXPLICIT: filters?.explicitMode === true ? 1 : 0,
							FROM_DATE: filters?.dateFrom || '',
							SORT: {
								FIELD: '',
								DIRECTION: '',
							},
							PAGE_NUM: currentPage,
							ROWS_PER_PAGE: RIGHTS_INVENTORY_ON_PAGE_ITEMS,
							MODE: view,
						};

						return this.rightsApiService.getInventory(params).pipe(
							concatMap((inventory) => [
								rightsActions.getPagesCount.succeeded({
									pagesCount: inventory.pagination.totalPages,
								}),
								rightsActions.getInventory.succeeded({inventory: inventory.data}),
							])
						);
					} else {
						return of(rightsActions.getInventory.succeeded({inventory: previousValue}));
					}
				}
			),
			catchError((error) => of(rightsActions.getInventory.failed({error})))
		)
	);

	/**
	 * This function creates an Angular effect that set inventory buffer.
	 * @returns an Observable that emits data when the effect completes
	 */
	setInventoryBuffer$ = createEffect(() =>
		this.actions$.pipe(
			ofType(rightsActions.copyInventoryToBuffer.requested),
			withLatestFrom(
				this.store.select(rightsSelectors.inventory.filters),
				this.store.select(rightsSelectors.inventory.data),
				this.store.select(rightsSelectors.inventory.pinned)
			),
			filter(([, filters, data]) => data?.length > 0 && !!filters?.text),
			concatMap(
				([, filters, data, oldBuffer]: [unknown, InventoryFilters, Inventory[], RightsInventoryBuffer]) => {
					const query = filters?.text || '*';
					const newBuffer = new Map(oldBuffer); // need to trigger STORE->selectors
					newBuffer.set(query, data);
					return [
						rightsActions.toggleInventoryXLSSelection.requested({key: query}),
						rightsActions.copyInventoryToBuffer.succeeded({buffer: newBuffer}),
					];
				}
			),
			catchError((error) => of(rightsActions.copyInventoryToBuffer.failed({error})))
		)
	);

	/**
	 * This function creates an Angular effect that modify inventory buffer.
	 * @returns an Observable that emits data when the effect completes
	 */
	modifyInventoryBuffer$ = createEffect(() =>
		this.actions$.pipe(
			ofType(rightsActions.modifyInventoryInBuffer.requested),
			withLatestFrom(this.store.select(rightsSelectors.inventory.pinned)),
			// filter(([, filters, oldBuffer]) => data?.length > 0 && !!filters?.text),
			concatMap(([{mod}, oldBuffer]) => {
				const newBuffer: RightsInventoryBuffer = new Map(oldBuffer);
				const newInventory = mod.exclude
					? newBuffer.get(mod.pinnedKey).filter((item: Inventory) => !mod.listIds.includes(item.uniqueId))
					: newBuffer.get(mod.pinnedKey).filter((item: Inventory) => mod.listIds.includes(item.uniqueId));

				if (newInventory?.length > 0) {
					newBuffer.set(mod.pinnedKey, newInventory);
					return [rightsActions.modifyInventoryInBuffer.succeeded({buffer: newBuffer})];
				} else {
					newBuffer.delete(mod.pinnedKey);
					if (!newBuffer.size) {
						return [
							rightsActions.modifyInventoryInBuffer.succeeded({buffer: newBuffer}),
							rightsActions.removeInventoryXLSSelection.requested({key: mod.pinnedKey}),
							rightsActions.getInventory.requested(),
						];
					}
					return [
						rightsActions.modifyInventoryInBuffer.succeeded({buffer: newBuffer}),
						rightsActions.removeInventoryXLSSelection.requested({key: mod.pinnedKey}),
					];
				}
			}),
			catchError((error) => of(rightsActions.modifyInventoryInBuffer.failed({error})))
		)
	);

	/**
	 * This function creates an Angular effect that remove inventory pinned.
	 * @returns an Observable that emits data when the effect completes
	 */
	removeInventoryPinned$ = createEffect(() =>
		this.actions$.pipe(
			ofType(rightsActions.removeInventoryFromBuffer.requested),
			withLatestFrom(this.store.select(rightsSelectors.inventory.pinned)),
			filter(([{query}]) => !!query),
			concatMap(([{query}, oldBuffer]: [any, RightsInventoryBuffer]) => {
				const newBuffer = new Map(oldBuffer); // need to trigger STORE->selectors
				newBuffer.delete(query);
				return [
					rightsActions.removeInventoryFromBuffer.succeeded({buffer: newBuffer}),
					rightsActions.removeInventoryXLSSelection.requested({key: query}),
				];
			}),
			catchError((error) => of(rightsActions.removeInventoryFromBuffer.failed({error})))
		)
	);

	/**
	 * This function creates an Angular effect that get x l s inventory.
	 * @returns an Observable that emits data when the effect completes
	 */
	getXLSInventory$ = createEffect(() =>
		this.actions$.pipe(
			ofType(rightsActions.inventoryXLS.requested),
			withLatestFrom(
				this.store.select(rightsSelectors.rightsSources.selected),
				this.store.select(rightsSelectors.inventory.filters),
				this.store.select(rightsSelectors.rightsSources.view),
				this.store.select(rightsSelectors.inventory.pinned),
				this.store.select(rightsSelectors.inventory.xlsSelection),
				this.store.select(rightsSelectors.workflowStatuses.data),
				this.store.select(rightsSelectors.productOwners.data)
			),
			filter(([, franchise, filters]) => !!franchise && !!filters),
			switchMap(
				([, franchise, filters, view, pinned, pinnedXLSSelection, workflowStatuses, productOwners]: [
					unknown,
					RightsSourcesOption,
					InventoryFilters,
					RightsView,
					RightsInventoryBuffer,
					string[],
					WorkflowStatus[],
					WorkflowStatus[]
				]) => {
					const params: RightsInventoryRequest = {
						FRANCHISE_GROUP_CODE: franchise.id,
						LEVEL_CODE: '',
						MEDIA_CODES: filters?.mediaType ?? [''],
						TERRITORY_CODES: filters?.territory ?? [''],
						LANGUAGE_CODES: filters?.language ?? [''],
						CHANNEL_CODES: filters?.channel ?? [''],
						GENRE_CODES: filters?.genre ?? [''],
						CONTENT_CODES: filters?.contentCode ?? [''],
						PROJECT_TYPE_CODES: filters?.projectCode ?? [''],
						PRODUCT_OWNER_CODES:
							productOwners.length === 2 ? [productOwners[1].id] : filters?.productCode ?? [''],
						WORKFLOW_STATUSES:
							filters?.workflowStatus?.[0] === '999'
								? workflowStatuses.slice(1).map(({id}) => +id)
								: filters?.workflowStatus?.map(Number) ?? [],
						SEARCH_STRING: filters?.text ?? '',
						EXCLUSIVITY: filters?.exclusivity.toUpperCase(),
						EXPLICIT: filters?.explicitMode === true ? 1 : 0,
						FROM_DATE: filters?.dateFrom || '',
						SORT: {
							FIELD: '',
							DIRECTION: '',
						},
						PAGE_NUM: null,
						ROWS_PER_PAGE: null,
						MODE: view,
					};

					const pinnedSelected = PinnedDataHelper.preparePinned(pinned, pinnedXLSSelection);
					const reportCode = 'RIGHTS_EXCEL_REPORT';

					return this.reportsApiService
						.generateReport(
							{
								reportCode,
								pinnedSelected,
								generateType: 'XLSX_INNER',
								...params,
							},
							{responseType: 'blob', cacheExpirationTime: 5 * 60 * 1000}
						)
						.pipe(
							map((data: Blob) => {
								const today = new Date().toISOString().split('T')?.[0] || '';
								const physicalName = params.MODE === RightsView.Physical ? '-INVENTORY' : '';
								const scheduleName = params.MODE === RightsView.Schedule ? '-AVAILABILITY' : '';
								const additionalName = physicalName || scheduleName;

								saveAs(
									data,
									`${pinnedXLSSelection.length ? 'PINNED_' : ''}RIGHTS_EXPORT_${
										params.MODE
									}${additionalName}_${today}.xlsx`
								);

								return rightsActions.inventoryXLS.succeeded({inventoryXLS: []});
							})
						);
				}
			),
			catchError((error) => of(rightsActions.inventoryXLS.failed({error})))
		)
	);

	/**
	 * This function creates an Angular effect that get pages count.
	 * @returns an Observable that emits data when the effect completes
	 */
	checkFilters$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(rightsActions.setInventoryFilters),
				withLatestFrom(
					this.store.select(rightsSelectors.inventory.filters),
					this.store.select(routerSelectors.router.queryParams)
				),
				switchMap(([, filters, query]) => {
					if (filters?.productCode.length === 1 && filters?.productCode[0] === '12' && !query.productCode) {
						// this.store.dispatch(routerActions.setQueryParams({payload: {params: {productCode: 'IFCE'}}}));
					}

					return EMPTY;
				})
			),
		{dispatch: false}
	);

	/**
	 * This function creates an Angular effect that get pages count.
	 * @returns an Observable that emits data when the effect completes
	 */
	getPagesCount$ = createEffect(() =>
		this.actions$.pipe(
			ofType(rightsActions.setInventoryFilters, rightsActions.setRightsView),
			map(() => rightsActions.getPagesCount.requested())
		)
	);

	/**
	 * This function creates an Angular effect that reset inventory.
	 * @returns an Observable that emits data when the effect completes
	 */
	resetInventory$ = createEffect(() =>
		this.actions$.pipe(
			ofType(
				rightsActions.rightsSources.failed,
				rightsActions.getMediaTypes.failed,
				rightsActions.getTerritories.failed,
				rightsActions.getLanguages.failed,
				rightsActions.getChannels.failed,
				rightsActions.getGenres.failed,
				rightsActions.getBrands.failed,
				rightsActions.getProjectTypes.failed,
				rightsActions.getProductOwners.failed,
				rightsActions.getWorkflowStatuses.failed
			),
			map(() => rightsActions.getInventory.failed({error: ''}))
		)
	);

	/**
	 * This function creates an Angular effect that apply filters and select page.
	 * @returns an Observable that emits data when the effect completes
	 */
	applyFiltersAndSelectPage$ = createEffect(() =>
		this.actions$.pipe(
			ofType(rightsActions.setInventoryFilters, rightsActions.setRightsView),
			withLatestFrom(this.store.select(rightsSelectors.isFiltersFailed)),
			filter(([, isFiltersFailed]) => !isFiltersFailed),
			concatMap(() => [rightsActions.selectPage({page: 1})])
		)
	);

	/**
	 * This function creates an Angular effect that apply filters.
	 * @returns an Observable that emits data when the effect completes
	 */
	applyFilters$ = createEffect(() =>
		this.actions$.pipe(
			ofType(rightsActions.setInventoryFilters, rightsActions.setRightsView),
			debounceTime(DEFAULT_API_CHANGED_MS),
			withLatestFrom(this.store.select(rightsSelectors.isFiltersFailed)),
			filter(([, isFiltersFailed]) => !isFiltersFailed),
			concatMap(() => [rightsActions.getInventory.requested()])
		)
	);

	/**
	 * This function creates an Angular effect that reset pinned on view changes.
	 * @returns an Observable that emits data when the effect completes
	 */
	resetPinnedOnViewChanges$ = createEffect(() =>
		this.actions$.pipe(
			ofType(rightsActions.setRightsView),
			concatMap(() => [rightsActions.resetPinnedInventory(), rightsActions.getProductOwners.requested()])
		)
	);

	/**
	 * This function creates an Angular effect that reset search results.
	 * @returns an Observable that emits data when the effect completes
	 */
	resetSearchResults$ = createEffect(() =>
		this.actions$.pipe(
			ofType(rightsActions.resetSearchResults),
			mapTo(rightsActions.getInventory.succeeded({inventory: null}))
		)
	);

	/**
	 * This function creates an Angular effect that reset pinned.
	 * @returns an Observable that emits data when the effect completes
	 */
	resetPinned$ = createEffect(() =>
		this.actions$.pipe(
			ofType(rightsActions.resetPinnedInventory),
			mapTo(rightsActions.removeInventoryFromBuffer.succeeded({buffer: new Map<string, Inventory[]>()}))
		)
	);

	/**
	 * This function creates an Angular effect that apply sorting.
	 * @returns an Observable that emits data when the effect completes
	 */
	applySorting$ = createEffect(() =>
		this.actions$.pipe(
			ofType(rightsActions.selectPage),
			debounceTime(DEFAULT_ACTIONS_DEBOUNCE_MS),
			withLatestFrom(this.store.select(rightsSelectors.isFiltersFailed)),
			filter(([, isFiltersFailed]) => !isFiltersFailed),
			map(() => rightsActions.getInventory.requested())
		)
	);

	/**
	 * This function creates an Angular effect that reset pinned on switch view.
	 * @returns an Observable that emits data when the effect completes
	 */
	resetPinnedOnSwitchView$ = createEffect(() =>
		this.actions$.pipe(
			ofType(rightsActions.setInventoryView),
			distinctUntilChanged(),
			withLatestFrom(this.store.select(rightsSelectors.isFiltersFailed)),
			filter(([, isFiltersFailed]) => !isFiltersFailed),
			concatMap(() => [
				rightsActions.selectPage({page: 1}),
				rightsActions.resetPinnedInventory(),
				rightsActions.getInventory.requested(),
			])
		)
	);

	/**
	 * This function creates an Angular effect that reset pagination.
	 * @returns an Observable that emits data when the effect completes
	 */
	resetPagination$ = createEffect(() =>
		this.actions$.pipe(
			ofType(rightsActions.setInventoryGrouping),
			concatMap(() => [rightsActions.selectPage({page: 1})])
		)
	);

	/**
	 * Constructor for RightsEffects class.
	 * @constructor
	 * @param {Actions} actions$ actions
	 * @param {RightsApiService} rightsApiService rights api service
	 * @param {ReportsApiService} reportsApiService reports api service
	 * @param {Store<AppState>} store store
	 */
	constructor(
		private actions$: Actions,
		private rightsApiService: RightsApiService,
		private reportsApiService: ReportsApiService,
		private store: Store<AppState>
	) {}

	/**
	 * Auxiliary method for accessing to export x l s.
	 * @param {Inventory[]} inventoryXLS
	 * @param {RightsInventoryBuffer} pinnedSearch
	 * @param {RightsInventoryRequest} params
	 * @returns {void}
	 */
	exportXLS(inventoryXLS: Inventory[], pinnedSearch: RightsInventoryBuffer, params: RightsInventoryRequest): void {
		const today = new Date().toISOString().split('T')?.[0] || '';
		const tab = RightsHelper.generateXLS(inventoryXLS, pinnedSearch, params, today);
		// debugger;
		ExportExcelService.exportFile([tab], `RIGHTS_EXPORT_${params.MODE}_${today}.xlsx`);
	}
}
